/**
 * MainLayout - Primary layout wrapper with header, main content, and footer
 */
import React, { memo, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import styles from './MainLayout.module.css';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { LoginPromptModal } from '@/components/common/LoginPromptModal';
import { CartDrawer } from '@/components/features/CartDrawer';
import { useAppSelector } from '@/hooks';
import { selectSeoSettings } from '@/store/slices/settingsSlice';

const MainLayout: React.FC = memo(() => {
  const seo = useAppSelector(selectSeoSettings);

  useEffect(() => {
    if (seo?.siteTitle) {
      document.title = seo.siteTitle;
    }
    if (seo?.metaDescription) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute('content', seo.metaDescription);
      }
    }
    if (seo?.metaKeywords) {
      let keywordsMeta = document.querySelector('meta[name="keywords"]');
      if (!keywordsMeta) {
        keywordsMeta = document.createElement('meta');
        keywordsMeta.setAttribute('name', 'keywords');
        document.head.appendChild(keywordsMeta);
      }
      keywordsMeta.setAttribute('content', seo.metaKeywords);
    }
  }, [seo]);

  return (
    <div className={styles.layout}>
      <Header />
      <main className={styles.main}>
        <div className={styles.container}>
          <Outlet />
        </div>
      </main>
      <Footer />
      <CartDrawer />
      <LoginPromptModal />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1E293B',
            color: '#F1F5F9',
            borderRadius: '12px',
            padding: '16px',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#FFFFFF',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#FFFFFF',
            },
          },
        }}
      />
    </div>
  );
});

MainLayout.displayName = 'MainLayout';

export default MainLayout;
